import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error("Could not send reset link", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-start justify-start px-6 md:px-16 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-md bg-teal-700 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">Audentra</div>
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-500">Compliance Hub</div>
          </div>
        </Link>

        {!sent ? (
          <>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Forgot your password?</h1>
            <p className="text-slate-600 mt-2">
              Enter the email tied to your Audentra account and we&apos;ll send you a reset link.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="label-caps">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full h-11 bg-teal-700 hover:bg-teal-800 rounded-full"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent">
            <div className="h-10 w-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-4">
              Check your inbox
            </h1>
            <p className="text-slate-600 mt-2">
              If <span className="font-semibold">{email}</span> is registered, we&apos;ve just sent a reset link. It will expire in 1 hour.
            </p>
          </div>
        )}

        <Link
          to="/login"
          data-testid="link-back-to-login"
          className="mt-8 inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
